import { type NotificationProps as CustomNotificationProps, Notification } from '@myosh/odin-components';
import React from 'react';
import ReactDOM from 'react-dom';
import toast, { type ToastOptions, useToaster } from 'react-hot-toast';

export interface NotificationProps
  extends ToastOptions, Pick<CustomNotificationProps, 'showCloseIcon' | 'onCloseClick'> {}

interface NotificationsProps {
  appendTo?: HTMLElement;
  toasterId?: string;
}

const Notifications = ({ appendTo, toasterId }: NotificationsProps) => {
  const { toasts, handlers } = useToaster({}, toasterId);
  const { startPause, endPause, calculateOffset, updateHeight } = handlers;

  const dismissToast = React.useCallback((id?: string) => {
    if (id) {
      toast.dismiss(id);
    }
  }, []);

  const createNotifications = () => {
    return (
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: 0,
          display: 'flex',
          width: '100%',
          zIndex: 9999,
          flex: '0 1 auto',
          justifyItems: 'center',
          pointerEvents: 'none',
        }}
      >
        {toasts.map((toast) => {
          const offset = calculateOffset(toast, { reverseOrder: false, gutter: 8 });

          const ref = (el: HTMLDivElement) => {
            if (el && !toast.height) {
              const height = el.getBoundingClientRect().height;
              updateHeight(toast.id, height);
            }
          };

          return (
            <div
              key={toast.id}
              ref={ref}
              {...toast.ariaProps}
              onMouseEnter={startPause}
              onMouseLeave={endPause}
              style={{
                position: 'absolute',
                left: '50%',
                top: '-1rem',
                transition: 'all 0.5s ease-out',
                opacity: toast.visible ? 1 : 0,
                transform: `translate(-50%, ${offset}px)`,
                zIndex: 9999999,
                pointerEvents: 'auto',
              }}
            >
              <Notification
                className="max-w-sm"
                type={toast.type}
                showCloseIcon={(toast as NotificationProps)?.showCloseIcon}
                onCloseClick={(toast as NotificationProps).onCloseClick ?? (() => dismissToast(toast.id))}
              >
                {String(toast.message)}
              </Notification>
            </div>
          );
        })}
      </div>
    );
  };

  return ReactDOM.createPortal(createNotifications(), appendTo ?? document.body);
};

export default Notifications;
